<script>import * as d3 from "d3";
import Picker from "./Picker.svelte";
import Button from "../../Button.svelte";
import { createEventDispatcher } from "svelte";
import MonthPicker from "./MonthPicker.svelte";
import YearPicker from "./YearPicker.svelte";
import {
  WeekNumberSystem,
  getDayLabels,
  getFirstWeekDay,
  getDayIndices,
  getWeekInterval,
  getMonthLabels,
  getFirstMonth,
  getMonthIndices,
  isSameDate,
  isBetween,
  getWeekOther
} from "../../../util/date-time-util.js";
const _browser = typeof window !== "undefined";
export let name = void 0;
export let locale = void 0;
export let system = WeekNumberSystem.ISO8601;
export let value = [void 0, void 0];
export let pick = "range";
export let makeSquare = false;
export let weeks = false;
const dispatch = createEventDispatcher();
value = value ?? [void 0, void 0];
let initialValue = [
  value[0] ? new Date(value[0].getTime()) : void 0,
  value[1] ? new Date(value[1].getTime()) : void 0
];
const now = /* @__PURE__ */ new Date();
$:
  dateInMonth = value && value[1] ? new Date(value[1].getTime()) : value[0] ? new Date(value[0].getTime()) : now;
$:
  startOfTheMonth = d3.timeMonth.floor(dateInMonth ?? /* @__PURE__ */ new Date());
$:
  startOfNextMonth = d3.timeMonth.offset(startOfTheMonth);
let startOfCurrentDay = d3.timeDay.floor(now);
let startOfNextDay = d3.timeDay.offset(startOfCurrentDay);
$:
  openMonth = dateInMonth.getMonth();
$:
  openYear = dateInMonth.getFullYear();
const getLocale = (locale2) => {
  return locale2 && locale2 !== "undefined" ? locale2 : _browser ? navigator.language : "en-US";
};
$:
  dayLabels = getDayLabels(getLocale(locale));
const firstWeekDay = getFirstWeekDay(system);
const dayIndices = getDayIndices(firstWeekDay);
const weekInterval = getWeekInterval(firstWeekDay);
$:
  monthLabels = getMonthLabels(getLocale(locale));
const firstMonth = getFirstMonth(system);
const monthIndices = getMonthIndices(firstMonth);
let showMonthPicker = false;
const getDayHeaderCell = (dayIndex) => {
  return {
    type: "header-top",
    marked: false,
    selected: false,
    inside: false,
    value: dayIndex,
    label: dayLabels.short[dayIndex]
  };
};
const getWeekHeaderCell = (weekNr, firstRow) => {
  return {
    type: "header-left",
    marked: false,
    selected: false,
    inside: false,
    value: weekNr,
    label: weekNr.week.toString(),
    valueTop: firstRow
  };
};
const getDayCell = (weekStart, offset, firstRow) => {
  const date = d3.timeDay.offset(weekStart, offset);
  return {
    type: date.getTime() >= startOfTheMonth.getTime() && date.getTime() < startOfNextMonth.getTime() ? "primary" : "secondary",
    marked: date.getTime() >= startOfCurrentDay.getTime() && date.getTime() < startOfNextDay.getTime(),
    selected: isSameDate(date, value[0]) || isSameDate(date, value[1]),
    inside: isBetween(date, value),
    disabled: false,
    value: date,
    label: date.getDate().toString(),
    valueTop: firstRow,
    valueLeft: offset === 0
  };
};
let cells = [];
$: {
  if (dayLabels) {
    let rows = 0;
    cells = weeks ? [
      {
        type: "empty",
        marked: false,
        selected: false,
        inside: false,
        value: void 0,
        label: ""
      }
    ] : [];
    dayIndices.map((dayIndex) => getDayHeaderCell(dayIndex)).forEach((cell) => cells.push(cell));
    rows++;
    let weekStart = weekInterval.floor(startOfTheMonth);
    do {
      const weekNr = getWeekOther(system, weekStart);
      if (weeks)
        cells.push(getWeekHeaderCell(weekNr, rows === 1));
      for (let i = 0; i < 7; i++) {
        cells.push(getDayCell(weekStart, i, rows === 1));
      }
      rows++;
      weekStart = weekInterval.offset(weekStart);
    } while (weekStart.getMonth() === startOfTheMonth.getMonth());
  }
}
const cellClicked = (ev) => {
  const index = ev.detail.index;
  const cell = ev.detail.cell;
  if (cell && !cell.disabled && (cell.type === "primary" || cell.type === "secondary")) {
    cells = cells.map((c, i) => {
      c.selected = i === index;
      return c;
    });
    if (pick === "start") {
      value[0] = cells[index].value;
    } else if (pick === "end") {
      value[1] = cells[index].value;
    } else {
      if (value[0] === void 0) {
        value[0] = cells[index].value;
        pick = "end";
      } else if (value[1] === void 0) {
        value[1] = cells[index].value;
        pick = "start";
      }
    }
    if (value[0] !== void 0 && value[1] !== void 0 && value[0].getTime() > value[1].getTime()) {
      const park = value[0];
      value[0] = value[1];
      value[1] = park;
    }
  }
  dispatch("action");
};
const jumpToYear = (year) => {
  dateInMonth.setFullYear(year);
  dateInMonth = new Date(dateInMonth.getTime());
  if (pick === "start" && value[0]) {
    value[0].setFullYear(year);
    value[0] = new Date(value[0].getTime());
  } else if (pick === "end" && value[1]) {
    value[1].setFullYear(year);
    value[1] = new Date(value[1].getTime());
  } else {
    if (value[0] !== void 0 && value[1] === void 0) {
      value[0].setFullYear(year);
      value[0] = new Date(value[0].getTime());
    } else if (value[0] === void 0 && value[1] !== void 0) {
      value[1].setFullYear(year);
      value[1] = new Date(value[1].getTime());
    }
  }
  if (value[0] !== void 0 && value[1] !== void 0 && value[0].getTime() > value[1].getTime()) {
    const park = value[0];
    value[0] = value[1];
    value[1] = park;
  }
  dispatch("action");
};
const jumpToMonth = (month) => {
  dateInMonth.setMonth(month);
  dateInMonth = new Date(dateInMonth.getTime());
  if (pick === "start" && value[0]) {
    value[0].setMonth(month);
    value[0] = new Date(value[0].getTime());
  } else if (pick === "end" && value[1]) {
    value[1].setMonth(month);
    value[1] = new Date(value[1].getTime());
  } else {
    if (value[0] !== void 0 && value[1] === void 0) {
      value[0].setMonth(month);
      value[0] = new Date(value[0].getTime());
    } else if (value[0] === void 0 && value[1] !== void 0) {
      value[1].setMonth(month);
      value[1] = new Date(value[1].getTime());
    }
  }
  if (value[0] !== void 0 && value[1] !== void 0 && value[0].getTime() > value[1].getTime()) {
    const park = value[0];
    value[0] = value[1];
    value[1] = park;
  }
  dispatch("action");
};
const dispatchValue = (value2) => {
  dispatch("changed", {
    name,
    value: value2
  });
};
</script>

<div class="date-picker">
	<div class="helpers">
		<MonthPicker
			value={openMonth}
			on:changed={(ev) => {
				if (ev.detail.value !== undefined) {
					jumpToMonth(ev.detail.value);
				}
			}}
			on:action
		/>
		<YearPicker
			value={openYear}
			on:changed={(ev) => {
				if (ev.detail.value !== undefined) {
					jumpToYear(ev.detail.value);
				}
			}}
			on:action
		/>
	</div>

	<Picker
		{cells}
		cellCharWidth={3}
		itemCharWidth={2}
		columns={weeks ? 8 : 7}
		{makeSquare}
		on:cell-click={cellClicked}
	/>
	<div class="buttons">
		<Button tab={false} story="secondary.small" on:click={() => dispatchValue(value)}>OK</Button>
		<Button tab={false} story="secondary.small" on:click={() => dispatchValue(initialValue)}
			>Cancel</Button
		>
	</div>
</div>

<style>
	div.date-picker {
		--base-color-onSurface-primary: #dbe4e7;
		--base-color-primary-default: #6bae85;
		--font-family: Roboto;
		--font-size: 1.25rem;
		--font-weight: 700;
		--letter-spacing: 0.0625rem;
	}
	div.date-picker {
		--picker-helper-font: normal var(--font-weight) var(--font-size) / 1.2 var(--font-family);
		--picker-helper-font-spacing: var(--letter-spacing);
		--picker-helper-text-color: var(--base-color-onSurface-primary);
		--picker-helper-focus-color: var(--base-color-primary-default);
		--picker-helper-button-color: var(--base-color-primary-default);
	}

	/*provides a left boundary for the picker fields which are absolute and have 'left' set to 0*/
	.date-picker > .helpers {
		position: relative;
	}

	.date-picker > .helpers,
	.date-picker > .buttons {
		margin: 0.5em 0;
	}
	.date-picker > .buttons > :global(*):first-child {
		margin-right: 2em;
	}
</style>
